import { VisibilityOutlined, Code, LocationOn, Language, CheckCircle } from "@mui/icons-material";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useNavigation } from "@refinedev/core";
import { CreateButton, useDataGrid } from "@refinedev/mui";
import { PortStatus } from "../../components/ports/status";
import { RefineListView } from "../../components/refine-list-view";
import { PortsIcon } from "../../components/icons/ports-icon";
import { useSingleFieldSearch } from "../../hooks/useSingleFieldSearch";
import { Port } from "../../types/port";

export const ListPorts = () => {
  const { dataGridProps, setFilters } = useDataGrid<Port>({
    syncWithLocation: true,
    filters: { mode: "server" },
    sorters: { mode: "server" },
    pagination: { mode: "server", pageSize: 10 },
    resource: "AdminPorts",
  });

  const columns: GridColDef<Port>[] = [
    { field: "id", headerName: "ID", minWidth: 90, flex: 1 },
    {
      field: "code",
      headerName: "Code",
      minWidth: 100,
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Code fontSize="small" />
          Code
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 300,
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOn fontSize="small" />
          Name
        </Box>
      ),
    },
    {
      field: "countryCode",
      headerName: "Country code",
      minWidth: 300,
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Language fontSize="small" />
          Country code
        </Box>
      ),
    },
    {
      field: "isDeleted",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CheckCircle fontSize="small" />
          Status
        </Box>
      ),
      renderCell: ({ row }) => <PortStatus port={row} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      hideable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      minWidth: 80,
      renderCell: function render({ row }) {
        return (
          <IconButton
            sx={{
              cursor: "pointer",
            }}
            onClick={() => edit("ports", row.id)}
          >
            <VisibilityOutlined color="action" />
          </IconButton>
        );
      },
    },
  ];

  const { searchValues, handleInputChange } = useSingleFieldSearch(
    {
      code: "",
      name: "",
      countryCode: "",
    },
    setFilters
  );

  const { edit } = useNavigation();

  const title = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <PortsIcon color="primary" />
      <Typography variant="h6">Ports</Typography>
    </Box>
  );

  return (
    <RefineListView
      title={title}
      headerButtons={
        <CreateButton key="create" size="medium" sx={{ height: "40px" }}>
          Add new port
        </CreateButton>
      }
    >
      <Box mb={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <TextField
            label="Code"
            value={searchValues.code}
            onChange={handleInputChange("code")}
            size="small"
          />
          <TextField
            label="Name"
            value={searchValues.name}
            onChange={handleInputChange("name")}
            size="small"
          />
          <TextField
            label="Country Code"
            value={searchValues.name}
            onChange={handleInputChange("countryCode")}
            size="small"
          />
        </Stack>
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          density: "standard",
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        disableDensitySelector
        onRowClick={({ id }) => {
          edit("ports", id);
        }}
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />
    </RefineListView>
  );
};
